import { QueryClient } from '@tanstack/react-query';
import { LoaderFunctionArgs } from 'react-router';
import { staffAnalyticsStatMetadataQueryOptions } from '~/hooks/queries/useAnalyticsStatMetadataQuery';
import { getStaffGlobalStatsQueryOptions } from '~/hooks/queries/useStaffGlobalStatsQuery';
import { getStaffReportTemplatesQueryOptions } from '~/hooks/queries/useStaffReportTemplatesQuery';
import {
  ANALYTICS_REPORTING_MODE_SEARCH_PARAM,
  parseGenie2ReportingModeSearchParam,
} from '~/utils/analyticsReportingMode';
import { prefetchQueryNonBlocking } from './prefetchQueryNonBlocking';
import { staffAdminPermsLoader } from './staffAdminPermsLoader';

export function prefetchStaffReportTemplatesLoader(queryClient: QueryClient) {
  const ensureStaffAdmin = staffAdminPermsLoader(queryClient);

  return async (args: LoaderFunctionArgs) => {
    await ensureStaffAdmin(args);
    const { statKey, templatePublicId } = args.params;
    const search =
      args.request?.url != null
        ? new URL(args.request.url).searchParams.get(ANALYTICS_REPORTING_MODE_SEARCH_PARAM)
        : null;
    const mode = parseGenie2ReportingModeSearchParam(search) ?? 'v2';

    prefetchQueryNonBlocking(
      queryClient,
      getStaffGlobalStatsQueryOptions({ analyticsReportingMode: mode }),
    );
    prefetchQueryNonBlocking(queryClient, getStaffReportTemplatesQueryOptions(undefined, mode));

    if (statKey) {
      prefetchQueryNonBlocking(queryClient, staffAnalyticsStatMetadataQueryOptions(statKey, mode));
    } else if (templatePublicId) {
      queryClient
        .ensureQueryData(getStaffReportTemplatesQueryOptions(undefined, mode))
        .then((data) => {
          const template = data?.templates?.find((t) => t.publicId === templatePublicId);
          if (template?.statKey) {
            prefetchQueryNonBlocking(
              queryClient,
              staffAnalyticsStatMetadataQueryOptions(template.statKey, mode),
            );
          }
        })
        .catch(() => undefined);
    }

    return null;
  };
}
